'use strict'

import { axiosGet } from '../../../axios/axios.js'
import {
  getAutoDebitTargetReport,
  buildAutoDebitPlan,
  applyAutoDebitPlan,
  isAutoDebitEligible,
  markAutoDebitsOffered
} from './ReportMaterialsAutoDebit.js'
import ReportMaterialsAutoDebitDialog from './ReportMaterialsAutoDebitDialog.vue'

export const autoDebitOfferMixin = {
  methods: {
    getAutoDebitRequest: function () {
      return this.request || null
    },
    getAutoDebitRootReport: function () {
      return this.report || null
    },
    saveAutoDebitChanges: async function () {
      if (typeof this.saveChanges === 'function') {
        return this.saveChanges()
      }
      const report = this.getAutoDebitRootReport()
      if (report && report._reportManager) {
        return report._reportManager.saveChanges({ target: this })
      }
      return false
    },
    offerAutoDebitIfNeeded: async function (targetReport, options = {}) {
      if (!isAutoDebitEligible(targetReport)) {
        return
      }

      const rootReport = this.getAutoDebitRootReport()
      if (!rootReport) {
        return
      }

      try {
        this.$q.loading.show({ spinnerColor: 'blue-11' })
        const context = await this.loadAutoDebitContext(targetReport)

        if (options.requireEmpty && !isAutoDebitEligible(targetReport, { requireEmpty: true })) {
          return
        }

        const plan = buildAutoDebitPlan({
          report: targetReport,
          standardSpecification: context.standardSpecification,
          materialesAvailable: context.materialesAvailable,
          projects: (rootReport.serviceObjectProjects || targetReport.serviceObjectProjects || [])
        })

        if (!plan.shouldRun) {
          return
        }

        if (plan.needsConfirm) {
          this.$q.loading.hide()
          const confirmed = await this.askAutoDebitConfirm(plan)
          if (!confirmed) {
            markAutoDebitsOffered(targetReport)
            return
          }
          this.$q.loading.show({ spinnerColor: 'blue-11' })
        }

        applyAutoDebitPlan(targetReport, plan)
        markAutoDebitsOffered(targetReport)
        await this.saveAutoDebitChanges()
      } catch (error) {
        console.log(error)
        this.$q.notify({
          type: 'negative',
          message: error.message || 'Ошибка автоматического списания',
          progress: true,
          timeout: 8000
        })
      } finally {
        this.$q.loading.hide()
      }
    },
    offerContractorAutoDebitOnReportOpen: async function () {
      const rootReport = this.getAutoDebitRootReport()
      if (!rootReport || !rootReport.brigadeIsContractor) {
        return
      }
      const state = rootReport.bpStateId
      if (state !== 'BS4') {
        return
      }

      const targetReport = getAutoDebitTargetReport(rootReport, this.getAutoDebitRequest())
      await this.offerAutoDebitIfNeeded(targetReport, { requireEmpty: true })
    },
    loadAutoDebitContext: async function (targetReport) {
      const rootReport = this.getAutoDebitRootReport()
      const reportId = targetReport.id
      const stocktoolsReportId = rootReport && rootReport.id ? rootReport.id : reportId
      const materialsGroups = targetReport._materialsGroups
      const tasks = []

      if (materialsGroups) {
        tasks.push(materialsGroups.getGroup('debits').loadRecords())
        tasks.push(materialsGroups.getGroup('consumptions').loadRecords())
      }

      let materialesAvailable = rootReport && rootReport._materialesAvailable
      let standardSpecification = targetReport._standardSpecification
      if (standardSpecification == null && rootReport && rootReport._standardSpecificationsByReport) {
        standardSpecification = rootReport._standardSpecificationsByReport[reportId] ||
          rootReport._standardSpecificationsByReport[String(reportId)] ||
          null
      }

      if (!materialesAvailable) {
        tasks.push(
          axiosGet({
            url: '/service/delta/report/' + stocktoolsReportId + '/stocktools',
            onSuccess: (response) => {
              materialesAvailable = response.data.result || []
              this.$set(rootReport, '_materialesAvailable', materialesAvailable)
            },
            onFailure: 'Ошибка получения доступных материалов',
            throws: true,
            msgTarget: this
          })
        )
      }

      if (standardSpecification == null) {
        tasks.push(
          axiosGet({
            url: '/service/delta/report/standardSpecification?reportIds=' + reportId,
            onSuccess: (response) => {
              const result = response.data.result || []
              const entry = result.find(item => item.reportId === reportId || String(item.reportId) === String(reportId))
              standardSpecification = entry ? (entry.items || []) : []
              this.$set(targetReport, '_standardSpecification', standardSpecification)
            },
            onFailure: 'Ошибка получения типовой спецификации',
            throws: true,
            msgTarget: this
          })
        )
      }

      if (tasks.length) {
        await Promise.all(tasks)
      }

      return {
        materialesAvailable: materialesAvailable || [],
        standardSpecification: standardSpecification || []
      }
    },
    askAutoDebitConfirm: function (plan) {
      return new Promise((resolve) => {
        const dialog = this.$d.dialog(this).show({
          maximizedOnNarrow: true,
          persistent: true,
          noWrap: true,
          contentWidth: 640,
          contentComponent: ReportMaterialsAutoDebitDialog,
          contentProps: {
            plan
          },
          componentHandlers: {
            confirm: () => {
              dialog.hide()
              resolve(true)
            },
            cancel: () => {
              dialog.hide()
              resolve(false)
            }
          }
        })
      })
    }
  }
}
